import type { RemoteLinks } from '@jira/issue__shared-types/src/TypesServerRemoteLinks.tsx';
import {
	fetchIssueViewRemoteData,
	fetchIssueViewRemoteDataAndTransform,
} from './fetchIssueViewRemoteData.tsx';
import type { RemoteDataGraphQlResponse } from './RemoteDataGraphQlResponse.types.tsx';

export class PrefetchedIssueViewRemoteDataManager {
	private cache = new Map<string, Promise<RemoteDataGraphQlResponse>>();

	prefetch(issueKey: string): void {
		const promise = fetchIssueViewRemoteData(issueKey);
		this.cache.set(issueKey, promise);
	}

	getOrFetch(issueKey: string): Promise<RemoteDataGraphQlResponse> | undefined {
		if (this.cache.has(issueKey)) {
			return this.cache.get(issueKey);
		}

		const promise = fetchIssueViewRemoteData(issueKey);
		this.cache.set(issueKey, promise);
		return promise;
	}

	consume(issueKey: string): Promise<RemoteDataGraphQlResponse> | undefined {
		const promise = this.getOrFetch(issueKey);
		this.cache.delete(issueKey);
		return promise;
	}

	fetchTransformAndStore(
		issueKey: string,
		cloudId: string,
	): Promise<{
		id: string;
		remoteLinks: RemoteLinks;
	}> {
		/**
		 * We use this function to fetch and transform the data for SSR,
		 * we then store the raw data for follow up calls to avoid duplicate calls
		 */
		return fetchIssueViewRemoteDataAndTransform(issueKey, cloudId, (rawDataPromise) =>
			this.cache.set(issueKey, rawDataPromise),
		);
	}
}

/**
 * Singleton instance to manage prefetched remote data for issue view.
 */
export const prefetchedRemoteDataManager: PrefetchedIssueViewRemoteDataManager =
	new PrefetchedIssueViewRemoteDataManager();
